import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Box, Grid } from '@mui/material';
import { formatRoute } from 'react-router-named-routes';
import SearchIcon from '@mui/icons-material/Search';
import {
  END_USER_RECIPE_COLLECTION_SEARCH_MEALS_ROUTE,
  END_USER_RECIPE_COLLECTION_ADD_MEALS_MULTI_MENUS_ROUTE,
  END_USER_RECIPE_COLLECTION_ADD_MEALS_BOARD_ROUTE,
} from '../../services/routes';
import TopNavBar from '../user/top_nav_bar';
import MealBasketWidget from '../../features/end_user_shopping/components/meal_basket_widget';
import PopupTitle from '../common/popup_title';
import RecipeCollectionsAddMealItem from './recipe_collections_add_meal_item';
import MealDetailModal from '../meal/meal_detail_modal';
import { linkWithQueryParams } from '../../services/urls';
import { useAllMultiMenuMeals } from '../common/all_multi_menu_meals_hook';
import {
  useGetSharedBoardsByIDsQuery,
  useGetDistilledSharedMealsByParentsQuery,
  useGetFullMealQuery,
  useGetFullSharedMealQuery,
} from '../../app/smorgAppSyncGraphQLApi';
import { DEFAULT_SPACE_ID } from '../../services/spaces';
import {
  myFavouritesRecipesBoard,
  parentIDOfSharedMealID,
} from '../../services/smorg_board';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetOwnedRecipesBoardsQuery,
  useGetSpaceAssignedSharedRecipesBoardIDsForEndUserSearchQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { SHARED_BOARDS_REFRESH_INTERVAL_SECONDS } from '../../services/end_user_published_resources';

const CAROUSEL_LIMIT = 10;

const showNutritionMetrics = [
  'calories',
  'fat',
  'carbohydrate',
  'protein',
  'fibre',
];

const RecipeCollectionsAddMealsHomePage = () => {
  const navigate = useNavigate();

  const { boardID: destinationBoardID, menuID: destinationMenuID } =
    useParams();

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const isCommunityUser = currentSpaceMembership?.spaceID === DEFAULT_SPACE_ID;

  const { data: ownedRecipesBoards } = useGetOwnedRecipesBoardsQuery();

  const myFavBoard = myFavouritesRecipesBoard(ownedRecipesBoards);

  const flattenedFavouriteMealIDs = (myFavBoard?.menus || []).flatMap((menu) =>
    (menu.mealIDs || menu.sharedMealIDs).map((mealID) => ({
      mealID,
      menuID: menu.id,
    })),
  );

  const [showDetailForMealID, setShowDetailForMealID] = useState(null);

  const onClickResultDetail = (mealID) => {
    setShowDetailForMealID(mealID);
  };

  const onDismissDetail = () => {
    setShowDetailForMealID(null);
  };

  const {
    data: spaceAssignedSharedRecipesBoardsIDs,
    isLoading: isSaceAssignedSharedRecipesBoardsIDsLoading,
  } = useGetSpaceAssignedSharedRecipesBoardIDsForEndUserSearchQuery();

  const { data: sharedBoards } = useGetSharedBoardsByIDsQuery(
    spaceAssignedSharedRecipesBoardsIDs,
    {
      skip:
        isSaceAssignedSharedRecipesBoardsIDsLoading ||
        !spaceAssignedSharedRecipesBoardsIDs ||
        spaceAssignedSharedRecipesBoardsIDs.length === 0,
      refetchOnMountOrArgChange: SHARED_BOARDS_REFRESH_INTERVAL_SECONDS,
    },
  );

  const sharedBoardIDs = useMemo(
    () => (sharedBoards || []).map((b) => b.id),
    [sharedBoards],
  );

  const parentIDInSharedMeals = parentIDOfSharedMealID(
    showDetailForMealID,
    sharedBoards,
  );

  const { data: distilledSharedMealsByParents } =
    useGetDistilledSharedMealsByParentsQuery(sharedBoardIDs, {
      skip: !sharedBoardIDs || sharedBoardIDs.length === 0,
    });

  const visibleDistilledMealInSharedMeals = ((distilledSharedMealsByParents ||
    {})[parentIDInSharedMeals] || {})[showDetailForMealID];

  const { flattenedSharedBoardsTitleMap } = useAllMultiMenuMeals();

  const alwaysShowNutrition = !isCommunityUser;
  const { data: fullOwnedMeal, isLoading: isFullOwnedMealLoading } =
    useGetFullMealQuery(showDetailForMealID, {
      skip: visibleDistilledMealInSharedMeals,
    });

  const { data: fullSharedMeal, isLoading: isFullSharedMealLoading } =
    useGetFullSharedMealQuery(showDetailForMealID, {
      skip: !visibleDistilledMealInSharedMeals,
    });

  const fullMeal = visibleDistilledMealInSharedMeals
    ? fullSharedMeal
    : fullOwnedMeal;
  const isFullMealLoading = visibleDistilledMealInSharedMeals
    ? isFullSharedMealLoading
    : isFullOwnedMealLoading;

  const isLoading = isFullMealLoading;

  if (showDetailForMealID && !isLoading && fullMeal) {
    return (
      <MealDetailModal
        fullMeal={fullMeal}
        isLoading={isLoading}
        menu={null}
        isReadOnly
        alwaysShowNutrition={alwaysShowNutrition}
        savingInProgress={false}
        showNutritionMetrics={showNutritionMetrics}
        derivedNutrition={fullMeal?.derivedNutrition}
        canScaleIngredients={false}
        scaleToServings={1}
        currentlyEditingInline={{ section: '', itemId: null }}
        ingredientSuggestions={[]}
        ingredientSuggestionsNetworkState={{ loading: false }}
        onInlineEditFocus={() => {}}
        onInlineEditChange={() => {}}
        onInlineEditBlur={() => {}}
        onAddArraySectionItem={() => {}}
        onRemoveArraySectionItem={() => {}}
        onIngredientCheckChange={() => {}}
        onEditScaleToServings={() => {}}
        onChangeScaleToServings={() => {}}
        resetScaleToServings={() => {}}
        onNewImageChosen={() => {}}
        onRuleChanged={() => {}}
        onAddonChange={() => {}}
        onTagsChange={() => {}}
        onNewImageUrlSet={() => {}}
        onDialogClose={onDismissDetail}
        onUpdateIngredientStructuredData={() => {}}
      />
    );
  }
  return (
    <div className="recipes-search">
      <TopNavBar />
      <div style={{ color: 'var(--app-primary-font-color)' }}>
        <PopupTitle
          titleText="Search for a meal"
          backButtonEnabled
          onClickBack={() => navigate(-1)}
        />
      </div>
      <MealBasketWidget />
      <div
        style={{
          marginTop: '20px',
        }}>
        <Grid container>
          <Grid
            item
            xs={12}
            onClick={() =>
              navigate(
                formatRoute(END_USER_RECIPE_COLLECTION_SEARCH_MEALS_ROUTE, {
                  boardID: destinationBoardID,
                  menuID: destinationMenuID,
                }),
              )
            }
            style={{
              padding: '12px',
              paddingLeft: '12px !important',
              textAlign: 'left',
              fontSize: '14px',
              color: 'var(--app-primary-font-color)',
              opacity: 0.5,
              border: '1px solid',
              borderRadius: '8px',
              margin: '8px 16px',
              cursor: 'pointer',
              display: 'flex',
            }}>
            <SearchIcon style={{ margin: 'auto 0' }} />
            <span style={{ paddingTop: '2px' }}>Search recipes</span>
          </Grid>
        </Grid>
        {myFavBoard && flattenedFavouriteMealIDs.length > 0 && (
          <>
            <Grid container>
              <Grid item xs={8}>
                <h2
                  style={{
                    margin: '23px 23px 5px',
                    color: 'var(--app-primary-font-color)',
                    textAlign: 'left',
                  }}>
                  {myFavBoard.title}
                </h2>
              </Grid>
              <Grid
                item
                xs={4}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}>
                <Box
                  sx={{
                    margin: 'auto 10px 7px',
                    color: 'var(--app-primary-font-color)',
                  }}>
                  <Link
                    to={formatRoute(
                      END_USER_RECIPE_COLLECTION_ADD_MEALS_BOARD_ROUTE,
                      {
                        boardID: destinationBoardID,
                        menuID: destinationMenuID,
                        recipesBoardId: myFavBoard.id,
                      },
                    )}>
                    View all
                  </Link>
                </Box>
              </Grid>
            </Grid>

            <div style={{ overflowX: 'auto', display: 'flex' }}>
              {flattenedFavouriteMealIDs
                .slice(0, CAROUSEL_LIMIT)
                .map(({ mealID }) => (
                  <div key={mealID} style={{ flexShrink: 0 }}>
                    <RecipeCollectionsAddMealItem
                      mealID={mealID}
                      destinationBoardID={destinationBoardID}
                      destinationMenuID={destinationMenuID}
                      onClick={onClickResultDetail}
                    />
                  </div>
                ))}
            </div>
          </>
        )}
        {Object.values(flattenedSharedBoardsTitleMap).map(
          ({ menus, menuTitle }) => {
            if (!menus || menus.length === 0) {
              return null;
            }
            const searchParamsArgs = {
              menuIDs: menus.map((m) => m.menuID),
              parentIDs: menus.map((m) => m.parentID),
            };
            return (
              <>
                <Grid container>
                  <Grid item xs={8}>
                    <h2
                      style={{
                        margin: '23px 23px 5px',
                        color: 'var(--app-primary-font-color)',
                        textAlign: 'left',
                      }}>
                      {menuTitle}
                    </h2>
                  </Grid>
                  <Grid
                    item
                    xs={4}
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}>
                    <Box
                      sx={{
                        margin: 'auto 10px 7px',
                        color: 'var(--app-primary-font-color)',
                      }}>
                      <Link
                        to={linkWithQueryParams(
                          formatRoute(
                            END_USER_RECIPE_COLLECTION_ADD_MEALS_MULTI_MENUS_ROUTE,
                            {
                              boardID: destinationBoardID,
                              menuID: destinationMenuID,
                            },
                          ),
                          searchParamsArgs,
                        )}>
                        View all
                      </Link>
                    </Box>
                  </Grid>
                </Grid>

                <div style={{ overflowX: 'auto', display: 'flex' }}>
                  {menus.flatMap((menu) =>
                    (menu.mealIDs || menu.sharedMealIDs)
                      .slice(0, CAROUSEL_LIMIT)
                      .map((mealID) => (
                        <div key={mealID} style={{ flexShrink: 0 }}>
                          <RecipeCollectionsAddMealItem
                            mealID={mealID}
                            destinationBoardID={destinationBoardID}
                            destinationMenuID={destinationMenuID}
                            onClick={onClickResultDetail}
                          />
                        </div>
                      )),
                  )}
                </div>
              </>
            );
          },
        )}
      </div>
    </div>
  );
};

export default RecipeCollectionsAddMealsHomePage;
